/**
 * Shared helpers for communicating with the iconik Manager Chrome/Safari
 * extension.
 *
 * Kept in a separate module (not in SelectOrganization.tsx) so that Header.tsx
 * can import bridge primitives without creating a circular dependency:
 *
 *   SelectOrganization.tsx  ←  extensionBridge.ts
 *   Header.tsx              ←  extensionBridge.ts
 *
 * No imports from other app modules here — this file is a dependency leaf.
 */

/** Message type the Safari content-script relay posts back to the page. */
export const SAFARI_BRIDGE_RESPONSE_TYPE = "ICONIK_BRIDGE_RESPONSE";

/**
 * Build a message payload suitable for both chrome.runtime.sendMessage and
 * window.postMessage (Safari bridge).
 */
export function buildBridgeMessage(
  type: string,
  data: Record<string, unknown> = {}
): Record<string, unknown> {
  return { ...data, type };
}

/**
 * Send a message to the Safari extension content-script bridge via postMessage.
 * Returns true if the channel is available, false if postMessage is absent.
 *
 * Does NOT wait for a response — register a listener before calling this
 * when a reply is needed.
 */
export function postMessageBridge(
  message: Record<string, unknown>,
  targetOrigin?: string
): boolean {
  if (
    typeof window === "undefined" ||
    typeof window.postMessage !== "function"
  ) {
    return false;
  }
  const origin = targetOrigin ?? window.location.origin;
  window.postMessage(message, origin);
  return true;
}
